// src/hooks/ui/useModalActionBeat.js
//
// The confirm-modal ACTION BEAT (founder 2026-08-19): submit → an in-modal
// progress phase → a short success beat → the bulk-style morph close. One
// machine for every "do the irreversible thing" modal (send invoice, send
// estimate, void) so the moment feels identical app-wide.
//
// Safety contract (the iOS frozen-modal rule, modal-freeze-ios-rpc-timeout):
//  - the awaited action is capped by withTimeout — a PWA backgrounded
//    mid-request can NEVER strand the modal in "working"; on timeout we close
//    and let the row state / toasts / reaper tell the truth (they do, since
//    the failed-send row-state + claim-reaper shipped).
//  - failure/timeout NEVER traps the user in the modal and never shows an
//    in-modal error: the caller\'s own toast + worklist pins are the message.
//  - reduced motion collapses the success hold to zero.
//
// Action-beats engine additions (WAVE 1, 2026-09-24) — both OPTIONAL,
// back-compat by default:
//  - `minWorkingMs` (default 0): the richer RING/FILL visuals need to be ON
//    screen a minimum time so a fast save doesn't flash through them
//    ("the ring never finishes sooner than 1.1s after the tap"). Success is
//    shown no earlier than `run()`'s start + minWorkingMs. With the default
//    0 this is always a no-op — `success` is set synchronously in the same
//    `.then()` tick exactly as before, so every existing caller (which never
//    passes this) is byte-identical.
//  - `holdMs` (default ACTION_BEAT_SUCCESS_HOLD_MS): how long the success
//    phase holds before `finish()`. Lets RING (900ms) / FILL (650ms) differ
//    from the legacy 650ms bar without callers re-implementing the hook.
//  - `false` / throw / timeout paths are UNCHANGED by either option — they
//    always finish immediately, never wait out minWorkingMs.

import { useCallback, useEffect, useRef, useState } from 'react';
import { withTimeout } from '../../utils/async/withTimeout';

export const ACTION_BEAT_TIMEOUT_MS = 20000;
export const ACTION_BEAT_SUCCESS_HOLD_MS = 650;

export function useModalActionBeat({
  playExit,
  onClose,
  reduced = false,
  minWorkingMs = 0,
  holdMs = ACTION_BEAT_SUCCESS_HOLD_MS,
}) {
  const [phase, setPhase] = useState('idle'); // idle | working | success
  const doneRef = useRef(false);
  // Double-fire guard IN THE HOOK (reviews 2026-08-19/20): a second call
  // before the footer swaps must never start a second write.
  const runningRef = useRef(false);
  // Hold-timer cleanup on unmount — a dead tree must not fire finish().
  const holdTimerRef = useRef(null);
  // Success-delay timer (minWorkingMs floor) — separate from the hold timer
  // so both can be cleared independently on unmount.
  const successTimerRef = useRef(null);
  const startedAtRef = useRef(0);
  useEffect(() => () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
  }, []);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    playExit(() => onClose?.());
  }, [playExit, onClose]);

  const scheduleHold = useCallback(() => {
    holdTimerRef.current = setTimeout(finish, reduced ? 0 : holdMs);
  }, [finish, reduced, holdMs]);

  const run = useCallback((action) => {
    if (doneRef.current || runningRef.current || typeof action !== 'function') return;
    runningRef.current = true;
    startedAtRef.current = Date.now();
    setPhase('working');
    withTimeout(Promise.resolve().then(action), ACTION_BEAT_TIMEOUT_MS)
      .then((result) => {
        if (doneRef.current) return;
        if (result === false) { finish(); return; } // handler-reported failure
        // Default (minWorkingMs 0, reduced false) always yields a delay <= 0,
        // so this stays the exact same synchronous setPhase('success') as
        // before — no extra macrotask, no behavior change.
        const elapsed = Date.now() - startedAtRef.current;
        const successDelay = reduced ? 0 : Math.max(0, minWorkingMs - elapsed);
        if (successDelay <= 0) {
          setPhase('success');
          scheduleHold();
        } else {
          successTimerRef.current = setTimeout(() => {
            if (doneRef.current) return;
            setPhase('success');
            scheduleHold();
          }, successDelay);
        }
      })
      .catch(() => { finish(); }); // throw/timeout — close, truth lives outside
  }, [finish, reduced, minWorkingMs, scheduleHold]);

  return { phase, run, isWorking: phase !== 'idle' };
}
